import type {
  CreateSolveInput,
  PaginatedResponse,
  ResourceIdentity,
  ResourceAudienceAccessProvenance,
  ResourceMemberPage,
  Solve,
  SolveCompletionReadiness,
  SolveCompletionRequirement,
  SolveDetail,
  SolvePhase,
  SolveRole,
  TenantDirectorySearchUser,
  UpdateSolveInput,
} from "@playbookai/types";
import {
  SOLVE_COMPLETION_REQUIREMENTS,
  SOLVE_COMPLETION_REQUIREMENT_LABELS,
} from "@playbookai/types";
import type { WidgetOperation } from "@playbookai/widget-schemas";
import { PlaybookApiError, apiCall } from "@playbookai/api-clients";
import type { ApiClientConfig } from "@playbookai/api-clients";

/**
 * SOLVE-specific playbook-be client. The generic fetch wrapper `apiCall` and
 * `buildApiUrl` graduated to `@playbookai/api-clients` (PB-263) once the second
 * live-fetch client (hoshin) needed them; they are re-exported from here so
 * existing deep imports (`./playbook-be-client.js`) and the package barrel keep
 * resolving them unchanged.
 */
export { PlaybookApiError, apiCall };
export type { ApiClientConfig };
export type { CurrentUserSnapshot } from "@playbookai/api-clients";

export type SolveMemberPage = Omit<
  ResourceMemberPage<SolveMemberResponse>,
  "totalCount"
> & {
  readonly totalCount?: number;
};

/**
 * The completion vocabulary is `@playbookai/types`', not this client's.
 *
 * It used to be declared here as well as on the server, and the two agreed
 * only by inspection: a server that added a fourth requirement first would
 * send an id this package's label map does not hold, and `useAdvanceSolvePhase`
 * would call `.toLowerCase()` on `undefined`. Re-exported rather than moved so
 * the popover and the advance warning keep importing from one place.
 *
 * `SolveCompletionReadiness.status` is `SolveStatus`, not `string` — the same
 * five-value enum the OpenAPI schema declares, so an unsupported comparison is
 * a compile error rather than a silently-false branch.
 */
export {
  SOLVE_COMPLETION_REQUIREMENTS,
  SOLVE_COMPLETION_REQUIREMENT_LABELS,
  type SolveCompletionRequirement,
  type SolveCompletionReadiness,
};

// --- Widget-typed convenience wrappers (cross-cutting) ---

export interface WidgetResponse {
  readonly id: string;
  readonly tenantId: string;
  readonly kind: string;
  readonly name: string;
  readonly description: string;
  readonly params: Record<string, unknown>;
  /** Public semantic params schema version used by widget contracts. */
  readonly schemaVersion: number;
  /** qa-004 optimistic-concurrency counter; echo it in the next PATCH. */
  readonly version: number;
  readonly visibility: string;
  readonly dataSourceRef: string | null;
  readonly createdBy: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface PlacementResponse {
  readonly id: string;
  readonly tenantId: string;
  readonly surface: string;
  readonly surfaceId: string;
  readonly position: Record<string, unknown> | null;
  /** The widget's phase on a solve_canvas; null = phase-agnostic / non-canvas. */
  readonly phase: string | null;
  readonly widget: WidgetResponse;
  readonly createdAt: string;
  readonly createdBy: string;
}

export interface CreateWidgetInput {
  readonly kind: string;
  readonly name: string;
  readonly description?: string;
  readonly params: Record<string, unknown>;
  readonly visibility?: "tenant" | "private";
}

export interface UpdateWidgetInput {
  readonly name?: string;
  readonly description?: string;
  readonly params?: Record<string, unknown>;
  readonly visibility?: "tenant" | "private";
  /**
   * qa-004: the client's last-seen `version` for optimistic-concurrency. When
   * sent, a stale value gets a 409 WIDGET_VERSION_CONFLICT instead of clobbering.
   */
  readonly version?: number;
  /**
   * Assign-person d25: a person was picked in a person field, so the save may
   * add them as Viewer. Only a pick sends it.
   */
  readonly grantViewerIfMissing?: boolean;
}

/**
 * PB-4923 — a bounded field/item change instead of a whole-params replacement.
 *
 * `commandId` is what makes a retry safe: the server returns the stored result
 * rather than applying twice, and refuses the same id carrying different
 * operations. It must therefore be minted ONCE per logical change and reused on
 * every retry of that change — never regenerated in a retry loop.
 */
export interface ApplyWidgetCommandInput {
  readonly commandId: string;
  /**
   * Typed as the executor's own operation rather than as loose JSON. A
   * `WidgetPathSegment` is a discriminated union, which is deliberately NOT
   * assignable to `Record<string, unknown>` — so an index-signature type here
   * would force a cast at every call site and let a malformed target through
   * the one place that could still catch it.
   */
  readonly operations: readonly WidgetOperation[];
  readonly baseVersion?: number;
  /** Assign-person d25, as on {@link UpdateWidgetInput}. */
  readonly grantViewerIfMissing?: boolean;
}

export interface WidgetCommandResult {
  readonly commandId: string;
  readonly widgetId: string;
  readonly version: number;
  readonly applied: ReadonlyArray<{
    readonly operationId: string;
    readonly kind: string;
    readonly targetKey: string;
  }>;
  /**
   * A skipped operation is an OUTCOME, not a failure — the call is a 200. A
   * caller that infers success from the status code will report a change that
   * did not happen.
   */
  readonly skipped: ReadonlyArray<{
    readonly operationId: string;
    readonly reason: string;
    readonly targetKey: string;
    /** Absent means unattributable — use neutral copy, never a guessed name. */
    readonly actorId?: string;
    readonly issues?: readonly string[];
  }>;
  readonly params: Record<string, unknown>;
  readonly replayed: boolean;
}

export interface WidgetEditSessionInput {
  readonly targetKey: string;
  readonly clientId: string;
}

export interface WidgetEditSessionsResult {
  readonly widgetId: string;
  readonly sessions: ReadonlyArray<{
    readonly targetKey: string;
    readonly userId: string;
  }>;
}

export interface UpsertPlacementInput {
  readonly widgetId: string;
  // home_dashboard is RETIRED (ADR-0054 §7): Personal Home lives in the
  // dashboard capability; the placement layer rejects the surface.
  readonly surface: "solve_canvas" | "player_ai_chat";
  readonly surfaceId: string;
  readonly position?: Record<string, unknown>;
  /**
   * The widget's phase on a solve_canvas — the upsert is the single
   * authoritative phase source (ADR-0019): send it on add and on cross-phase
   * moves so server-side phase-lock resolution works. Omit for non-canvas
   * surfaces; send an explicit null when a placed widget moves to a
   * phase-agnostic spot (clears the persisted phase).
   */
  readonly phase?: string | null;
}

export type SolveMemberRole = Extract<
  SolveRole,
  "owner" | "collaborator" | "viewer"
>;
export type GrantableSolveMemberRole = Exclude<SolveMemberRole, "owner">;

export interface SolveMemberResponse {
  readonly userId: string;
  readonly role: SolveMemberRole;
  readonly joinedAt: string;
  readonly accessSources?: readonly ResourceAudienceAccessProvenance[];
}

export interface AddSolveMemberInput {
  readonly userId: string;
  readonly role: GrantableSolveMemberRole;
}

export interface FacilitatorResponse {
  readonly solveId: string;
  readonly userId: string;
  readonly grantedBy: string;
  readonly grantedAt: string;
}

export const widgetsApi = {
  list(
    config: ApiClientConfig,
    query: {
      kind?: string;
      q?: string;
      /**
       * Inclusive incremental-pull bound (PB-148). Must be a truncated ISO date
       * (`2026-08-12`) or a date-time with an explicit offset
       * (`2026-08-12T00:00:00Z`) — the server 400s basic format and offset-less
       * values, and rejects a non-calendar date such as `2026-02-30`. Cannot be
       * combined with `cursor` (400).
       *
       * Deliberately absent from `mockWidgetsApi.list`, which does not implement
       * the filter. That divergence is documentation, NOT enforcement: the mock is
       * a standalone literal with no `satisfies typeof widgetsApi`, and nothing
       * dispatches between the two, so omitting the field cannot produce a compile
       * error today. Whoever wires the mock/real swap for PB-145 should add that
       * `satisfies` clause if the divergence needs to be load-bearing.
       */
      updatedSince?: string;
      limit?: number;
      cursor?: string;
    } = {},
  ): Promise<{
    data: WidgetResponse[];
    nextCursor: string | null;
    hasMore: boolean;
  }> {
    return apiCall(
      config,
      "GET",
      "/v1/widgets",
      undefined,
      query as Record<string, string | number | undefined>,
    );
  },

  get(config: ApiClientConfig, widgetId: string): Promise<WidgetResponse> {
    return apiCall(config, "GET", `/v1/widgets/${widgetId}`);
  },

  create(
    config: ApiClientConfig,
    input: CreateWidgetInput,
  ): Promise<WidgetResponse> {
    return apiCall(config, "POST", "/v1/widgets", input);
  },

  update(
    config: ApiClientConfig,
    widgetId: string,
    input: UpdateWidgetInput,
  ): Promise<WidgetResponse> {
    return apiCall(config, "PATCH", `/v1/widgets/${widgetId}`, input);
  },

  delete(config: ApiClientConfig, widgetId: string): Promise<void> {
    return apiCall(config, "DELETE", `/v1/widgets/${widgetId}`);
  },

  /** PB-4923 — apply bounded operations instead of replacing `params`. */
  applyCommand(
    config: ApiClientConfig,
    widgetId: string,
    input: ApplyWidgetCommandInput,
  ): Promise<WidgetCommandResult> {
    return apiCall(config, "POST", `/v1/widgets/${widgetId}/commands`, input);
  },

  /** Open or renew this caller's editing session. Also the heartbeat. */
  openEditSession(
    config: ApiClientConfig,
    widgetId: string,
    input: WidgetEditSessionInput,
  ): Promise<WidgetEditSessionsResult> {
    return apiCall(
      config,
      "POST",
      `/v1/widgets/${widgetId}/edit-sessions`,
      input,
    );
  },

  /** Release it. Best-effort — expiry is the real cleanup. */
  closeEditSession(
    config: ApiClientConfig,
    widgetId: string,
    input: WidgetEditSessionInput,
  ): Promise<WidgetEditSessionsResult> {
    return apiCall(
      config,
      "POST",
      `/v1/widgets/${widgetId}/edit-sessions/release`,
      input,
    );
  },
};

export const widgetPlacementsApi = {
  moveBatch(
    config: ApiClientConfig,
    input: {
      surface: string;
      surfaceId: string;
      placements: Array<{
        widgetId: string;
        position: Record<string, unknown>;
        phase?: string | null;
      }>;
    },
  ): Promise<PlacementResponse[]> {
    return apiCall(config, "POST", "/v1/widget-placements/batch", input);
  },
  list(
    config: ApiClientConfig,
    query: { surface: string; surfaceId: string },
  ): Promise<PlacementResponse[]> {
    return apiCall(
      config,
      "GET",
      "/v1/widget-placements",
      undefined,
      query as Record<string, string | number | undefined>,
    );
  },

  upsert(
    config: ApiClientConfig,
    input: UpsertPlacementInput,
  ): Promise<PlacementResponse> {
    return apiCall(config, "POST", "/v1/widget-placements", input);
  },

  delete(config: ApiClientConfig, placementId: string): Promise<void> {
    return apiCall(config, "DELETE", `/v1/widget-placements/${placementId}`);
  },
};

// --- SOLVE-typed convenience wrappers ---

export const solvesApi = {
  list(
    config: ApiClientConfig,
    query: {
      limit?: number;
      cursor?: string;
      q?: string;
      access?: import("@playbookai/types").ModuleAccessOrigin;
    } = {},
  ): Promise<PaginatedResponse<Solve>> {
    return apiCall(
      config,
      "GET",
      "/v1/solves",
      undefined,
      query as Record<string, string | number | undefined>,
    );
  },

  get(
    config: ApiClientConfig,
    solveId: string,
    signal?: AbortSignal,
  ): Promise<SolveDetail> {
    return apiCall(
      config,
      "GET",
      `/v1/solves/${solveId}`,
      undefined,
      undefined,
      signal,
    );
  },

  listMembers(
    config: ApiClientConfig,
    solveId: string,
  ): Promise<SolveMemberResponse[]> {
    return apiCall(config, "GET", `/v1/solves/${solveId}/members`);
  },

  pageMembers(
    config: ApiClientConfig,
    solveId: string,
    cursor?: string,
    signal?: AbortSignal,
  ): Promise<SolveMemberPage> {
    return apiCall(
      config,
      "GET",
      `/v1/solves/${solveId}/members/page`,
      undefined,
      cursor ? { cursor } : undefined,
      signal,
    );
  },

  searchDirectory(
    config: ApiClientConfig,
    solveId: string,
    query: string,
    cursor?: string,
    signal?: AbortSignal,
  ): Promise<ResourceMemberPage<TenantDirectorySearchUser>> {
    return apiCall(
      config,
      "POST",
      `/v1/solves/${solveId}/members/directory/search`,
      { query, ...(cursor ? { cursor } : {}) },
      undefined,
      signal,
    );
  },

  resolveIdentities(
    config: ApiClientConfig,
    solveId: string,
    userIds: readonly string[],
    signal?: AbortSignal,
  ): Promise<readonly ResourceIdentity[]> {
    return apiCall(
      config,
      "POST",
      `/v1/solves/${solveId}/members/identities/resolve`,
      { userIds },
      undefined,
      signal,
    );
  },

  addMember(
    config: ApiClientConfig,
    solveId: string,
    input: AddSolveMemberInput,
  ): Promise<SolveMemberResponse> {
    return apiCall(config, "POST", `/v1/solves/${solveId}/members`, input);
  },

  updateMemberRole(
    config: ApiClientConfig,
    solveId: string,
    userId: string,
    role: GrantableSolveMemberRole,
  ): Promise<SolveMemberResponse> {
    return apiCall(config, "PATCH", `/v1/solves/${solveId}/members/${userId}`, {
      role,
    });
  },

  removeMember(
    config: ApiClientConfig,
    solveId: string,
    userId: string,
  ): Promise<void> {
    return apiCall(config, "DELETE", `/v1/solves/${solveId}/members/${userId}`);
  },

  grantFacilitator(
    config: ApiClientConfig,
    solveId: string,
    userId: string,
  ): Promise<FacilitatorResponse> {
    return apiCall(config, "PUT", `/v1/solves/${solveId}/facilitator`, {
      userId,
    });
  },

  revokeFacilitator(config: ApiClientConfig, solveId: string): Promise<void> {
    return apiCall(config, "DELETE", `/v1/solves/${solveId}/facilitator`);
  },

  widgets(
    config: ApiClientConfig,
    solveId: string,
  ): Promise<PlacementResponse[]> {
    return apiCall(config, "GET", `/v1/solves/${solveId}/widgets`);
  },

  create(config: ApiClientConfig, input: CreateSolveInput): Promise<Solve> {
    return apiCall(config, "POST", "/v1/solves", input);
  },

  update(
    config: ApiClientConfig,
    solveId: string,
    input: UpdateSolveInput,
  ): Promise<Solve> {
    return apiCall(config, "PATCH", `/v1/solves/${solveId}`, input);
  },

  returnPhase(
    config: ApiClientConfig,
    solveId: string,
    expectedPhase: string,
  ): Promise<Solve> {
    return apiCall(config, "POST", `/v1/solves/${solveId}/return-phase`, {
      expectedPhase,
    });
  },

  advancePhase(config: ApiClientConfig, solveId: string): Promise<SolveDetail> {
    return apiCall(config, "POST", `/v1/solves/${solveId}/advance-phase`);
  },

  /**
   * What the SOLVE still owes before it can be completed (PB-5272).
   *
   * `view`-gated server-side, so a Collaborator or Viewer can see the
   * checklist even though only the Owner or Facilitator can act on it. The
   * authoritative gate is `close` below, which re-reads the same rule under a
   * row lock — this read informs the dialog, it does not decide anything.
   */
  completionReadiness(
    config: ApiClientConfig,
    solveId: string,
  ): Promise<SolveCompletionReadiness> {
    return apiCall(config, "GET", `/v1/solves/${solveId}/completion-readiness`);
  },

  /**
   * Complete the SOLVE. The wire verb stays `close` and the resulting status
   * stays `Closed` — "Complete" is the user's word for the same terminal
   * transition, and renaming the endpoint would break every existing client
   * for a label change.
   */
  close(config: ApiClientConfig, solveId: string): Promise<SolveDetail> {
    return apiCall(config, "POST", `/v1/solves/${solveId}/close`);
  },

  // v2 lock toggles (ADR-0019). Both are idempotent (locked:true locks,
  // false unlocks). The canvas toggle returns the updated Solve (base fields
  // incl. the new canvasLocked) — NOT the capability snapshot, which only the
  // detail read populates; a caller refreshes affordances by merging
  // canvasLocked into the cached SolveDetail (or refetching). The phase toggle
  // returns just the affected phase's new state. `solveId` (a validated UUID)
  // and `phase` (a fixed SolvePhase enum) are interpolated raw, consistent with
  // every other path in this client.
  setCanvasLock(
    config: ApiClientConfig,
    solveId: string,
    locked: boolean,
  ): Promise<Solve> {
    return apiCall(config, "PUT", `/v1/solves/${solveId}/locks/canvas`, {
      locked,
    });
  },

  setPhaseLock(
    config: ApiClientConfig,
    solveId: string,
    phase: SolvePhase,
    locked: boolean,
  ): Promise<{ phase: SolvePhase; locked: boolean }> {
    return apiCall(
      config,
      "PUT",
      `/v1/solves/${solveId}/locks/phases/${phase}`,
      {
        locked,
      },
    );
  },

  delete(config: ApiClientConfig, solveId: string): Promise<void> {
    return apiCall(config, "DELETE", `/v1/solves/${solveId}`);
  },
};
